import React, { useRef, useState } from 'react';
import { masterDataService, MasterDataResource } from '../../services/masterDataService';
import {
  formatMasterDataImportMessage,
  MasterDataImportResult,
  todayExportSuffix,
} from '../../utils/masterDataIO';
import { useToast } from '../../contexts/ToastContext';

interface MasterDataImportExportProps {
  resource: MasterDataResource;
  onComplete?: () => void;
  className?: string;
  fileNamePrefix?: string;
}

const ExportIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
    />
  </svg>
);

const ImportIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 14l5-5m0 0l5 5m-5-5v11"
    />
  </svg>
);

export const MasterDataImportExport: React.FC<MasterDataImportExportProps> = ({
  resource,
  onComplete,
  className = '',
  fileNamePrefix,
}) => {
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);

  const prefix = fileNamePrefix || resource;

  const handleExport = async () => {
    try {
      setExporting(true);
      const data = await masterDataService.export(resource);
      const recordCount = Array.isArray(data)
        ? data.length
        : (data as { record_count?: number }).record_count ?? (data as { records?: unknown[] }).records?.length ?? 0;
      showToast(`Exported ${recordCount} record(s)`, 'success');
      onComplete?.();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Export failed';
      showToast(message, 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      setImporting(true);
      const result = (await masterDataService.import(resource, file)) as MasterDataImportResult;
      const hasErrors = Boolean(result.error) || Boolean(result.errors?.length) || (result.failed ?? 0) > 0;

      showToast(formatMasterDataImportMessage(result), hasErrors ? 'error' : 'success');
      if (!hasErrors) {
        onComplete?.();
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Import failed';
      showToast(message, 'error');
    } finally {
      setImporting(false);
    }
  };

  const iconButtonClass = `
    inline-flex items-center justify-center p-2.5 rounded-md border transition-colors
    focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed
    border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200
    hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-indigo-700 dark:hover:text-indigo-300
  `;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <button
        type="button"
        onClick={handleExport}
        disabled={exporting || importing}
        className={iconButtonClass}
        title={`Export ${prefix.replace(/_/g, ' ')}`}
        aria-label={`Export ${prefix.replace(/_/g, ' ')}`}
      >
        <ExportIcon />
      </button>
      <button
        type="button"
        onClick={handleImportClick}
        disabled={exporting || importing}
        className={iconButtonClass}
        title={`Import ${prefix.replace(/_/g, ' ')}`}
        aria-label={`Import ${prefix.replace(/_/g, ' ')}`}
      >
        <ImportIcon />
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={handleImportFile}
      />
    </div>
  );
};

export { todayExportSuffix };
